import { computedFrom } from 'aurelia-binding';
import { HttpClient } from 'aurelia-fetch-client';
import { autoinject } from 'aurelia-framework';
import { extend } from 'aurelia-i18n';
import 'whatwg-fetch';
import { ApiResponse } from '../models/api-response';
import { Payment } from '../models/customer';
import { DigitalWalletType } from '../models/digital-wallet';
import { ServiceResponse } from '../models/service-response';
import { State } from './global-state';
import { HPFServiceClient } from './hpfApi';

@autoinject
export class HpfTransactionService extends HPFServiceClient {

	constructor(
		state: State,
		http: HttpClient,
	) {
		super(state, http);
	}

	submitTransaction(request: SubmitTransactionRequest): Promise<ServiceResponse> {
		return this.post('/transaction', request)
			.then((apiResponse: ApiResponse) => {
				if (!apiResponse.isSuccessful) {
					try {
						return this.error(`Unable to process transaction: ${apiResponse.getErrorMessage()}`);
					} catch (error) {
						if (apiResponse.errors && apiResponse.errors.length > 0) {
							return this.error(`Unable to process transaction: ${apiResponse.errors[0]}`);
						}
						return this.error(`Unable to process transaction`);
					}
				}
				return this.success('', apiResponse.value);
			});
	}
}

export class SubmitTransactionRequest {
	transactionType: TransactionTypes;
	paymentMethod: PaymentMethod;
	invoicePayments: IInvoice[] = []; // not used yet
	accountPayments: ICustomerAccountToPay[];
	paymentDate: string;
	amount: number;
	discountAmount: number | null;
	convenienceFeeOverrideAmount: number | null = null; // no overrides by consumer in portal;
	merchantKey: number;
	description: string;
	saveToWalletForCustomerKey: number | null;
	postedInfo: PostedInfo;
	relatedCustomerKey: number | null;
	serviceFeeAmount: number | null; // informational field used by digital wallets
	isPayOffEarly: boolean;
	paymentPlanId: string;
}

export class SubmitTransactionResponse {
	isSuccessful: string;
	paymentId: string;
	resultCode: string;
	resultMessage: string;
}

export enum TransactionTypes {
	Sale = 1,
	SingleFuture = 2
}

// ---------------------------------------------------------------------------------
// The following classes were copied over from HPF and are in general
// models/enums/interfaces folders in that project. For now, just keep them here
// in portal and refactor later once/if needed outside of this file.
// ---------------------------------------------------------------------------------

export class PaymentMethod {
	customerKey: number = -1;
	paymentMethodType: PaymentMethodType = PaymentMethodType.NotSet;
	paymentMethodKey: number | null = null;
	isPrimaryPaymentMethod: boolean = false;
	accountName: string = '';
	accountNumber: string = '';
	addressLine1: string = '';
	city: string = '';
	state: string = '';
	zipCode: string = '';
	paymentAccountNickname: string = '';

	get paymentMethodTypeName(): string {
		return 'Unknown';
	}

	deserialize(input: any): PaymentMethod {
		let self: any = this;
		for (let prop in input) {
			self[prop] = input[prop];
		}
		return self;
	}
}

export enum PaymentMethodType {
	NotSet = -1,
	ECheck = 0,
	Card = 1,
	DigitalWallet = 2,
	Credit = 3,
	Debit = 4
}

export enum CardNetworkNameType {
	NotSet = -1,
	Amex = 0,
	Discover = 1,
	MasterCard = 2,
	Visa = 3,
	Other = 99
}

export class CardPaymentMethod extends PaymentMethod {
	expirationMonth: number = 0;
	expirationYear: number = 0;
	cvv: string = '';
	cardNetworkName: string = '';
	cardNetworkNameType: CardNetworkNameType = CardNetworkNameType.NotSet;
	isThirdParty: boolean = false;

	constructor(paymentMethodKey?: number, cvv?: string, zipCode?: string, expirationMonth?: number, expirationYear?: number, cardNetworkNameType?: CardNetworkNameType, isThirdParty?: boolean) {
		super();
		this.paymentMethodType = PaymentMethodType.Card;
		this.paymentMethodKey = paymentMethodKey;
		this.cvv = cvv;
		this.zipCode = zipCode;
		this.expirationMonth = expirationMonth;
		this.expirationYear = expirationYear;
		this.cardNetworkNameType = cardNetworkNameType;
		this.isThirdParty = isThirdParty;
	}

	@computedFrom('cardNetworkName')
	get paymentMethodTypeName(): string {
		return this.cardNetworkName;
	}

	deserialize(input: any): CardPaymentMethod {
		let self: any = this;
		for (let prop in input) {
			self[prop] = input[prop];
		}
		return self;
	}
}

export class CheckPaymentMethod extends PaymentMethod {
	achAccountType: AchAccountTypeEnum;
	aba: string = '';
	checkNumber = '';

	constructor(paymentMethodKey?: number, zipCode?: string) {
		super();
		this.achAccountType = AchAccountTypeEnum.PersonalChecking;
		this.paymentMethodType = PaymentMethodType.ECheck;
		this.paymentMethodKey = paymentMethodKey;
		this.zipCode = zipCode;
	}

	@computedFrom('achAccountType')
	get paymentMethodTypeName(): string {
		switch (this.achAccountType) {
			case AchAccountTypeEnum.PersonalChecking:
				return 'Personal Checking';
			case AchAccountTypeEnum.PersonalSavings:
				return 'Personal Savings';
			case AchAccountTypeEnum.BusinessChecking:
				return 'Business Checking';
		}
	}

	deserialize(input: any): CheckPaymentMethod {
		var self: any = this;
		for (let prop in input) {
			self[prop] = input[prop];
		}
		return self;
	}
}

export class DigitalWalletPaymentMethod extends PaymentMethod {
	isCreditCard: boolean;
	isDebitCard: boolean;
	walletMerchantId: string;
	digitalWalletType: DigitalWalletType;
	authenticationPaymentToken: string;
	cardType: string;
	customFields: string[] = []; // ApplePayToDo: implement?
	calculatedServiceFee: number; // informational field of the fee amount in the payment token

	constructor() {
		super();
		this.paymentMethodType = PaymentMethodType.DigitalWallet;
	}
}

export enum AchAccountTypeEnum {
	PersonalChecking = 1,
	PersonalSavings = 2,
	BusinessChecking = 3
}

export interface IInvoice {
	invoiceKey: number;
	customerAccountKey: number;
	paymentAmount: number;
	merchantKey: number;
	paymentBalanceType: TransactionBalancePaymentType;
	paymentLimitSettings: PaymentLimitSettings;
}

export interface ICustomerAccountToPay {
	customerAccountKey: number;
	paymentAmount: number;
	discountAmount: number;
	merchantKey: number;
	paymentBalanceType: TransactionBalancePaymentType;
	paymentLimitSettings: PaymentLimitSettings;
	minPaymentDue: number;
	referenceId: string;
	customerName: string;
}

export enum TransactionBalancePaymentType {
	NotSet = 0,
	FullPayment = 1,
	ShortPayment = 2,
	OverPayment = 3
}

export class PaymentLimitSettings {
	portfolioKey: number = -1;
	allowShortPay: boolean = true;
	requireShortPayReason: boolean = false;
	enableShortPayUserComment: boolean = false;
	allowOverPay: boolean = false;
	enableOverPayWarningMessage: boolean = false;
	shortPayReasons: string[] = [];
	selectedShortPayReason: number = -1;
	shortPayComment: string = '';
	minimumPaymentAmount: number = 0 || null;
	maximumPaymentAmount: number = 0 || null;
	enableMinimumPaymentDueOnAccount: boolean = false;
}

export class PostedInfo {
	username: string = '';
	password: string = '';
	returnUrl: string = '';
	ackUrl: string = '';
	userType: string = '';
	referenceTransactionId: string = '';
	urlType: string = '';
	userId: string = null;
	invoiceComment: string = '';
	accountEmailAddress: string = '';
	paymentCategory: string = '';

	get displayPaymentCategory(): boolean {
		return (this.paymentCategory && this.userType.toLowerCase() === 'a');
	}
}

export enum UserType {
	NotSet = 0,
	Agent = 1,
	Consumer = 2
}